import { isAfter, fromUnixTime } from 'date-fns';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { RootState } from 'App/Store';
import { getUrlParam } from 'utils/urlUtils';

import dealersApi from 'modules/dealers/service';
import { getLoginToken } from 'modules/dealers/selectors';
import { ConnectionTypeLogin } from 'modules/dealers/types/ContextResponse';

import { formatPhoneNumber, getDefaultChannelToSendCode, getRetryExpiration } from 'modules/communication/utils';
import authApi from './service';
import { LoginParams } from './types/LoginRequest';
import { LoginResponse, SendMFACodeResponse } from './types/LoginResponse';
import { hasValidQrCode } from './selectors';

type InitLoginResponse = SendMFACodeResponse | LoginResponse;

export const login = createAsyncThunk<InitLoginResponse, LoginParams>(
  'login/init',
  async ({
    registration, connectionTypes, phoneNumber, locationId, qrCode, type, codeInputDisabled, ...mfaParams
  }, { dispatch, rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const token = getUrlParam('token');
    const contextToken = getLoginToken(state);

    try {
      const { exp } = jwtDecode<JwtPayload>(contextToken);
      const currentDate = new Date();
      const expirationDate = fromUnixTime(exp);
      const isTokenExpired = isAfter(currentDate, expirationDate);

      // When token expired because user didn't refresh, refetch context to get new exp token
      if (isTokenExpired) {
        await dispatch(dealersApi.endpoints.getContext.initiate(undefined, { forceRefetch: true })).unwrap();
      }

      if (hasValidQrCode(state) && !codeInputDisabled) {
        return await dispatch(authApi.endpoints.sendQrCode.initiate({ type, qrCode })).unwrap();
      }

      const isLoginMFA = connectionTypes?.includes(ConnectionTypeLogin.MFA);

      if (isLoginMFA) {
        const computedPhoneNumber = formatPhoneNumber(phoneNumber);

        const data = await dispatch(authApi.endpoints.getMFAConnection.initiate({
          type,
          registration,
          phoneNumber: computedPhoneNumber,
          ...mfaParams,
        })).unwrap();

        const channel = getDefaultChannelToSendCode(data);
        const retryExpiration = getRetryExpiration(data, channel);

        if (!retryExpiration || isAfter(new Date(), new Date(retryExpiration))) {
          const { connectionId, selfServiceId } = data;
          return await dispatch(authApi.endpoints.sendMFACode.initiate({
            connectionId,
            selfServiceId,
            channel,
          })).unwrap();
        }
        return undefined;
      }

      if (token) {
        return await dispatch(authApi.endpoints.login.initiate({
          registration,
          locationId,
        })).unwrap();
      }
    } catch (error) {
      return rejectWithValue(error);
    }
    return rejectWithValue(undefined);
  },
);
